import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, setStoredToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing token");
    if (pw1.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw1 !== pw2) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password-reset-confirm", { token, new_password: pw1 });
      setStoredToken(data.token);
      await refresh();
      toast.success("Password updated. You're signed in.");
      nav(data.user?.is_admin ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="reset-password-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink text-white relative overflow-hidden grain">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-white text-ink grid place-items-center"><Sparkles size={16} /></div>
          <span className="font-display font-bold text-lg">Zenith Ranking</span>
        </Link>
        <div>
          <div className="eyebrow !text-white/50 mb-3">Set a new password</div>
          <h2 className="font-display text-4xl font-bold tracking-tight">Pick something strong. You'll be inside in a moment.</h2>
        </div>
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-terra/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="eyebrow mb-3">New password</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Reset your password</h1>
          <p className="mt-2 text-sm text-inkSoft">Enter and confirm a new password (6+ characters).</p>
          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="reset-form">
            <Input label="New password" type="password" value={pw1} onChange={setPw1} testid="reset-pw1" />
            <Input label="Confirm password" type="password" value={pw2} onChange={setPw2} testid="reset-pw2" />
            <button type="submit" disabled={loading || !token} className="btn-primary w-full justify-center mt-2" data-testid="reset-submit">
              {loading ? "Saving…" : (<>Update password <ArrowUpRight size={16} /></>)}
            </button>
            {!token && <p className="text-xs text-red-600 text-center">Missing token in URL.</p>}
          </form>
        </div>
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-inkSoft">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
        data-testid={testid}
      />
    </label>
  );
}
